import { useState } from "react";
import { Button } from "@/design-system/primitives";
import {
  useDoseChangeHandledCheck,
  useMarkDoseChangeHandled,
  type DoseChangeHold,
} from "@/lib/api/doseChangeHolds";

/** Close a request a prescriber already answered: an order for this treatment
 * carries a prescriber's approval and a new prescription recorded after the
 * request. The option only appears when the server finds that evidence; the
 * decision stays the prescriber's. */
export function AlreadyHandledDoseChange({
  hold,
  auto = true,
}: {
  hold: DoseChangeHold;
  /** Check on mount (patient card) or on demand (worklist queue). */
  auto?: boolean;
}) {
  const [requested, setRequested] = useState(auto);
  const check = useDoseChangeHandledCheck(hold.patient_id, hold.id, requested);
  const mark = useMarkDoseChangeHandled(hold.patient_id);
  // Confirmation is for one exact prescription; a refreshed check showing a
  // different one needs a fresh confirmation.
  const [confirmedFor, setConfirmedFor] = useState<string | null>(null);
  const [note, setNote] = useState("");

  if (!requested)
    return (
      <button
        type="button"
        className="underline text-14 self-start min-h-44"
        onClick={() => setRequested(true)}
      >
        Check if already handled
      </button>
    );
  if (check.isPending)
    return (
      <p role="status" className="text-14 text-ink-muted">
        Checking for a new prescription…
      </p>
    );
  if (check.isError)
    return (
      <p role="alert" className="text-14 text-danger">
        Couldn&apos;t check for a new prescription.{" "}
        <button type="button" className="underline" onClick={() => void check.refetch()}>
          Retry
        </button>
      </p>
    );
  const data = check.data;
  if (!data?.available)
    return (
      <p className="text-12 text-ink-muted">
        {data?.reason || "Still needs the prescriber's review."}{" "}
        <button
          type="button"
          className="underline"
          disabled={check.isFetching}
          onClick={() => void check.refetch()}
        >
          Check again
        </button>
      </p>
    );
  const target = `${data.order_id}:${data.medication_id}`;
  const confirmed = confirmedFor === target;
  const approvedAt = data.approved_at
    ? new Date(data.approved_at).toLocaleString()
    : null;
  return (
    <form
      className="flex flex-col gap-8 border border-line p-10 bg-raised"
      onSubmit={(event) => {
        event.preventDefault();
        if (!confirmed || mark.isPending || !data.order_id || !data.medication_id) return;
        mark.mutate({
          requestId: hold.id,
          body: {
            confirmed: true,
            note: note.trim(),
            expected_resolution_version: hold.resolution_version,
            expected_order_id: data.order_id,
            expected_medication_id: data.medication_id,
          },
        });
      }}
    >
      <p className="text-14 font-medium">Already handled?</p>
      <p className="text-14">
        {data.approved_by || "A prescriber"} approved a changed prescription
        {data.order_number ? ` on ${data.order_number}` : ""}
        {approvedAt ? ` · ${approvedAt}` : ""}, after this request came in.
      </p>
      <dl className="text-14 grid grid-cols-[auto_1fr] gap-x-10 gap-y-4">
        <dt className="text-ink-muted">Before</dt>
        <dd>
          {data.previous_medication_name || "Earlier prescription"}
          {data.previous_directions ? (
            <span className="block text-12 text-ink-muted">{data.previous_directions}</span>
          ) : null}
        </dd>
        <dt className="text-ink-muted">Now</dt>
        <dd>
          <strong>{data.medication_name || "New prescription"}</strong>
          {data.directions ? (
            <span className="block text-12 text-ink-muted">{data.directions}</span>
          ) : null}
        </dd>
      </dl>
      <label className="text-14 flex gap-8">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmedFor(event.target.checked ? target : null)}
        />
        This prescription answers what the patient asked for. Close the
        request; future fills use this prescription.
      </label>
      <label className="text-14 flex flex-col gap-5">
        Note (optional)
        <textarea
          maxLength={2000}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          className="bg-page border border-line p-10"
        />
      </label>
      <Button type="submit" className="self-start" disabled={!confirmed || mark.isPending}>
        Mark already handled
      </Button>
      {mark.isError && (
        <p role="alert" className="text-14 text-danger">
          {mark.error instanceof Error
            ? mark.error.message
            : "The request could not be closed. Refresh and retry."}
        </p>
      )}
    </form>
  );
}
